import { DeviceInfoDrawer, SearchDevice } from '@/entities/device';
import { DevicesTable } from '@/widgets/devices-table';
import { CustomTabs } from '@/shared/ui/components';
import { TitleComponent } from '@/widgets/page-header';
import { DEVICE_TABLE_TYPE } from '@/shared/config';
import { auth } from '@/shared/lib';
import classNames from 'classnames';
import { t } from 'ttag';
import './devices.scss';
import { facilitySelection } from '@/entities/facility';
import { useDevices } from './useDevices';
import DevicesTableWithSubTab from '../devicesTableWithSubTab/devicesTableWithSubTab';

/**
 * Renders the devices page: the tabbed device inventory plus the device info drawer it opens. Takes its state and handlers from useDevices, adds a marketing- or logo-banner offset class when auth reports one, renders the three device tabs, and shows the facility selector and device search only when the user has support facilities.
 *
 * @returns The devices page markup.
 */
const DevicesPage = () => {
  const {
    state,
    supportFacilitiesRedux,
    selectedFacilityRedux,
    gotoRedux,
    headerRef,
    updateSubTabGoto,
    navigateTab,
    toggleDisplayDrawer,
    onChange,
  } = useDevices();

  /**
   * Builds the three top-level device tabs: all devices, lost/inactive and unused. Each entry wires its table to the active-tab flag, the search and select values kept on the navigation state, the selected and support facilities, the model filter and the drawer toggle, using the sub-tab variant for the all-devices and unused tabs.
   *
   * @returns The tab descriptors for the devices page tab bar.
   */
  const listTabDevice = () => [
    {
      key: '1',
      id: 'all-devices',
      label: <div>{DEVICE_TABLE_TYPE.ALL_DEVICES}</div>,
      children: (
        <DevicesTableWithSubTab
          isActive={state.activeTab === '1'}
          type={DEVICE_TABLE_TYPE.ALL_DEVICES_TAB}
          toggleDisplayDrawer={toggleDisplayDrawer}
          updateSubTabGoto={updateSubTabGoto}
          subTab={gotoRedux?.[DEVICE_TABLE_TYPE.ALL_DEVICES_TAB]}
          searchValue={state.searchValue}
          searchByValue={state.searchByValue}
          searchGoto={gotoRedux.search}
          selectedFacility={selectedFacilityRedux}
          supportFacilities={supportFacilitiesRedux ?? undefined}
          selectGoto={gotoRedux.select}
          model={state.model}
          isTicketCountVisible
        />
      ),
    },
    {
      key: '2',
      id: 'lost-inactive-devices',
      label: <div>{DEVICE_TABLE_TYPE.LOST_INACTIVE}</div>,
      children: (
        <DevicesTable
          isActive={state.activeTab === '2'}
          type={DEVICE_TABLE_TYPE.LOST_INACTIVE}
          toggleDisplayDrawer={toggleDisplayDrawer}
          searchValue={state.searchValue}
          searchByValue={state.searchByValue}
          searchGoto={gotoRedux.search}
          selectedFacility={selectedFacilityRedux}
          supportFacilities={supportFacilitiesRedux ?? undefined}
          selectGoto={gotoRedux.select}
          model={state.model}
          isTicketCountVisible
        />
      ),
    },
    {
      key: '3',
      id: 'unused-devices',
      label: <div>{DEVICE_TABLE_TYPE.UNUSED}</div>,
      children: (
        <DevicesTableWithSubTab
          isActive={state.activeTab === '3'}
          type={DEVICE_TABLE_TYPE.UNUSED_DEVICES_TAB}
          toggleDisplayDrawer={toggleDisplayDrawer}
          updateSubTabGoto={updateSubTabGoto}
          subTab={gotoRedux?.[DEVICE_TABLE_TYPE.UNUSED_DEVICES_TAB]}
          searchValue={state.searchValue}
          searchByValue={state.searchByValue}
          searchGoto={gotoRedux.search}
          selectedFacility={selectedFacilityRedux}
          supportFacilities={supportFacilitiesRedux ?? undefined}
          selectGoto={gotoRedux.select}
          model={state.model}
          isTicketCountVisible
        />
      ),
    },
  ];

  return (
    <>
      <DeviceInfoDrawer
        visible={state.isVisibleDeviceInfoDrawer}
        deviceId={(state.clickedDevice as any)?.deviceId}
        handleClickCloseButton={() => {
          toggleDisplayDrawer();
        }}
      />

      <div
        ref={headerRef}
        id="devices-page-id"
        className={classNames('custom-page devices-page animated fadeIn', {
          'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
        })}
      >
        <div className="top-container">
          <TitleComponent
            title={t`Devices`}
            isShowListFacility={
              facilitySelection.userSupportFacilities()?.length > 0 ||
              (supportFacilitiesRedux?.length ?? 0) > 0
            }
          />
        </div>
        <div className="top-nav-tabs-container">
          <CustomTabs
            className="devices-tab"
            tabTitleClassName="tab-title"
            activeKey={state.activeTab}
            onChange={navigateTab}
            items={listTabDevice() as any}
          />
          {(facilitySelection.userSupportFacilities()?.length > 0 ||
            (supportFacilitiesRedux?.length ?? 0) > 0) && (
            <SearchDevice model={state.model} searchValue={state.searchValue} onChange={onChange} />
          )}
        </div>
      </div>
    </>
  );
};

export default DevicesPage;
